import React, { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import { StepHeading, ContinueButton, CtaRow, Item, CheckBadge, Loader, SubView } from "../NOKit";

// iOS NOFirstAdventureStep + NOStoryGenerationStep port. A lesson (free text
// or one-tap chip) OR a ready-made adventure — mutually exclusive. The CTA
// runs completeOnboarding() behind a cosmetic "story being made" loader; the
// choice is stashed as a CreateStory draft so their first visit to /create is
// prefilled.
const IDEA_CHIPS = [
  "Sharing with friends",
  "Being brave",
  "Telling the truth",
  "Trying new foods",
  "Bedtime is fun",
  "Kindness wins",
];

// Generated theme art (gpt-image, Pixley story-cover style), bundled as
// local assets; emoji tile is the fallback if an image fails to load.
export const ADVENTURES = [
  { id: "pirate_treasure", emoji: "🏴‍☠️", title: "Pirate Treasure Hunt", thumb: "/assets/adventure-pirate.jpg" },
  { id: "space_mission", emoji: "🚀", title: "Space Mission", thumb: "/assets/adventure-space.jpg" },
  { id: "dino_discovery", emoji: "🦕", title: "Dinosaur Discovery", thumb: "/assets/adventure-dino.jpg" },
  { id: "ocean_quest", emoji: "🌊", title: "Ocean Quest", thumb: "/assets/adventure-ocean.jpg" },
];

// Cosmetic wait while completeOnboarding saves the account — no story is
// generated here; the pick is stashed as a draft for the Create Story page.
const MAKING_MESSAGES = [
  "Setting up your Pixley world…",
  "Saving their character…",
  "Getting the first adventure ready…",
];

const AdventureStep = () => {
  const { username, setAdventure, completeOnboarding, isLoading } = useOnboarding();
  const [lesson, setLesson] = useState("");
  const [theme, setTheme] = useState(null);
  const [making, setMaking] = useState(false);
  const [broken, setBroken] = useState({});

  const name = username || "your child";
  const canContinue = (lesson.trim().length >= 3 || !!theme) && !making && !isLoading;

  const handleMake = async () => {
    if (!canContinue) return;
    const adventure = theme
      ? { theme: theme.id, theme_title: theme.title }
      : { lesson: lesson.trim() };
    setAdventure(adventure);
    setMaking(true);
    try {
      await completeOnboarding(adventure);
    } finally {
      setMaking(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {making || isLoading ? (
        <Loader
          key="making"
          clip="panda_paint_2"
          title={<>{username ? `${username}'s` : "Their"} first story is <span className="fs-accent">being made</span></>}
          messages={MAKING_MESSAGES}
        />
      ) : (
        <SubView key="pick">
          <StepHeading
            eyebrow="First story"
            title={`What should ${name}`}
            accent="learn?"
            subtitle="Type a lesson, tap an idea, or pick a ready-made adventure."
          />

          <Item className="fs-field-wrap">
            <label className={`fs-field${theme ? " is-muted" : ""}`} style={theme ? { opacity: 0.6 } : undefined}>
              <span className="fs-field-label">A lesson or an idea</span>
              <textarea
                className="ob-textarea"
                value={lesson}
                maxLength={140}
                rows={2}
                placeholder="e.g. why brushing teeth is important"
                onChange={(e) => {
                  setLesson(e.target.value);
                  if (e.target.value.trim()) setTheme(null);
                }}
              />
            </label>
          </Item>

          <Item className="ob-ideas" role="group" aria-label="Ideas">
            {IDEA_CHIPS.map((chip) => (
              <button
                key={chip}
                type="button"
                aria-pressed={lesson === chip}
                className={`ob-idea${lesson === chip ? " is-selected" : ""}`}
                onClick={() => {
                  setLesson(chip);
                  setTheme(null);
                }}
              >
                {chip}
              </button>
            ))}
          </Item>

          <Item className="fs-divider">
            <span>Or pick a ready-made adventure</span>
          </Item>

          <Item className="ob-adventures" role="radiogroup" aria-label="Ready-made adventures">
            {ADVENTURES.map((a) => {
              const selected = theme?.id === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={a.title}
                  className={`ob-adventure${selected ? " is-selected" : ""}`}
                  onClick={() => {
                    setTheme(selected ? null : a);
                    if (!selected) setLesson("");
                  }}
                >
                  {broken[a.id] ? (
                    <span className="ob-adventure-fallback" aria-hidden="true">{a.emoji}</span>
                  ) : (
                    <img
                      src={a.thumb}
                      alt=""
                      loading="lazy"
                      onError={() => setBroken((b) => ({ ...b, [a.id]: true }))}
                    />
                  )}
                  <span className="ob-adventure-scrim">{a.emoji} {a.title}</span>
                  {selected && <CheckBadge />}
                </button>
              );
            })}
          </Item>

          <CtaRow>
            <ContinueButton
              onClick={handleMake}
              disabled={!canContinue}
              label={username ? `Make ${username}'s first story` : "Make the first story"}
            />
          </CtaRow>
        </SubView>
      )}
    </AnimatePresence>
  );
};

export default AdventureStep;
